/**
 * Enhances a list of nodes by adding a tracker object to each node.
 *
 * This function takes an array of nodes and maps through each node, adding a `tracker` object
 * that contains additional metadata. The `tracker` object includes the node's unique identifier (`id`),
 * a default `isChecked` state, and an array of parsed actions derived from the node's available certification actions.
 *
 * @function addTrackerObject
 * @param {Array<Object>} nodes - An array of node objects to be processed.
 * @param {string} nodes[].certKey - The unique key for the certification node.
 * @param {string} [nodes[].availableCertificationAction] - A comma-separated string of available actions for the node.
 * @returns {Array<Object>} - A new array of nodes with an added `tracker` object for each node.
 *
 * @description
 * - The `tracker` object contains the following properties:
 *   - `id`: Derived from the `certKey` of the node.
 *   - `isChecked`: A boolean initialized to `false` for bulk action checkbox for tile.
 *   - `isConnected`: A boolean initialized to `false` for connected bar to sidepanel.
 *   - `actions`: An array of actions parsed from the `availableCertificationAction` string. If no actions are available, it defaults to an empty array.
 *
 * @example
 * const nodes = [
 *   { certKey: '123', availableCertificationAction: 'approve,reject' },
 *   { certKey: '456', availableCertificationAction: '' },
 * ];
 * const enhancedNodes = addTrackerObject(nodes);
 * console.log(enhancedNodes);
 * // Output:
 * // [
 * //   {
 * //     certKey: '123',
 * //     availableCertificationAction: 'approve,reject',
 * //     tracker: { id: '123', isChecked: false, isConnected: false, actions: ['approve', 'reject'] },
 * //   },
 * //   {
 * //     certKey: '456',
 * //     availableCertificationAction: '',
 * //     tracker: { id: '456', isChecked: false, isConnected: false, actions: [] },
 * //   }
 * // ]
 */
export const addTrackerObject = (nodes) =>
  nodes.map((node) => ({
    ...node,
    tracker: {
      id: node?.certkey,
      isChecked: false,
      isConnected: false,
      isTransition: false,
      actions: node.availableCertificationAction
        ? [...node.availableCertificationAction.split(',')]
        : [],
    },
  }));

export default addTrackerObject;
